<template>
  <Input
    :model-value="getValue"
    type="text"
    :placeholder="options.placeholder"
    :label="options.label"
    :aria-label="options.label"
    variant="narrow"
    @update:model-value="setValue"
  >
    <template #icon>
      <component :is="options.icon" v-if="options.icon" />
      <SearchIcon v-else />
    </template>
  </Input>
</template>

<script lang="ts" setup>
import { computed, type FunctionalComponent } from 'vue';
import { useVModel, type VueInstance } from '@vueuse/core';
import Input from '@components/utils/inputs/Input.vue';
import SearchIcon from '@images/search.svg?component';
import { syncRef } from '@/hooks/state';

export interface Options {
  label?: string;
  placeholder?: string;
  icon?: VueInstance | FunctionalComponent | string;
  transformIn?: <Value>(value: Value) => string | number | undefined;
  transformOut?: <Value>(value?: string | number) => Value;
}

export interface Props<Value = unknown> {
  modelValue: Record<string, Value>;
  filterKey: string;
  options: Options;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const transformIn = computed(() => props.options.transformIn ?? (<Value>(v: Value) => v));
const transformOut = computed(() => props.options.transformOut ?? ((v?: string | number) => v));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const internalValue = syncRef<Record<string, any>, string | number | undefined>(value, (v) => transformIn.value(
  v[props.filterKey],
));

const getValue = computed(() => internalValue.value);

const setValue = (incomingValue?: string | number) => {
  internalValue.value = incomingValue;
  value.value[props.filterKey] = transformOut.value(incomingValue);
};
</script>

<style lang="scss" scoped></style>
